import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';

/**
 * Creates a manual Finance adjustment at row or provider scope.
 * onConfirm({ payment_row_id?, provider_id?, amount, adjustment_type, reason }).
 * `override` is only offered for row scope (server applies it per-row).
 */
function AdjustmentDialog({ open, rows = [], providerTotals = [], onClose, onConfirm, submitting }) {
  const [scope, setScope] = useState('row');
  const [targetId, setTargetId] = useState('');
  const [type, setType] = useState('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) { setScope('row'); setTargetId(''); setType('add'); setAmount(''); setReason(''); }
  }, [open]);

  // Provider scope doesn't support override (server-side).
  useEffect(() => { if (scope === 'provider' && type === 'override') setType('add'); }, [scope, type]);

  const providerOptions = providerTotals.filter((p) => p.provider_id);
  const amountNum = Number(amount);
  const valid = targetId && reason.trim() && amount !== '' && !Number.isNaN(amountNum);

  const submit = () => {
    const body = { amount: amountNum, adjustment_type: type, reason: reason.trim() };
    if (scope === 'row') body.payment_row_id = targetId;
    else body.provider_id = targetId;
    onConfirm(body);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add adjustment</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Scope</InputLabel>
            <Select value={scope} label="Scope" onChange={(e) => { setScope(e.target.value); setTargetId(''); }}>
              <MenuItem value="row">Row</MenuItem>
              <MenuItem value="provider">Provider</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel>{scope === 'row' ? 'Row' : 'Provider'}</InputLabel>
            <Select value={targetId} label={scope === 'row' ? 'Row' : 'Provider'} onChange={(e) => setTargetId(e.target.value)}>
              {scope === 'row'
                ? rows.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      #{r.source_row_number} — {r.uploaded_provider_name || '(no provider)'} (${Number(r.calculated_payment_amount || 0).toFixed(2)})
                    </MenuItem>
                  ))
                : providerOptions.map((p) => (
                    <MenuItem key={p.provider_id} value={p.provider_id}>{p.provider_name}</MenuItem>
                  ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
              <MenuItem value="add">Add</MenuItem>
              <MenuItem value="deduct">Deduct</MenuItem>
              {scope === 'row' && <MenuItem value="override">Override (set exact amount)</MenuItem>}
            </Select>
          </FormControl>

          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            required
          />
          <TextField label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} multiline rows={2} required />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" disabled={!valid || submitting} onClick={submit}>
          {submitting ? 'Saving…' : 'Add adjustment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AdjustmentDialog;
